import React, { useEffect, useLayoutEffect, useRef } from "react";
import House from "../../assets/gallery/Home.png";
import Car from "../../assets/gallery/Car.png";
import BubbleFX from "./BubbleFX";

export default function Home() {
  const carRef = useRef(null);

  useLayoutEffect(() => {
    const el = document.querySelector("[data-sticky-nav]") || document.querySelector("nav");
    const setNavH = () => {
      const h = el ? el.offsetHeight : 0;
      document.documentElement.style.setProperty("--nav-h", `${h}px`);
    };
    setNavH();
    const ro = el ? new ResizeObserver(setNavH) : null;
    ro?.observe(el);
    window.addEventListener("resize", setNavH);
    window.addEventListener("orientationchange", setNavH);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", setNavH);
      window.removeEventListener("orientationchange", setNavH);
    };
  }, []);

  useEffect(() => {
    const el = carRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          el.classList.add("drive");
          document.documentElement.classList.add("hero-text-in");
        }
      },
      { threshold: 0.3 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <main id="home" className="hero" aria-label="Hero">
      <BubbleFX
        density={0.4}
        maxRadius={38}
        speed={0.9}
        hueShift={-4}
        opacity={0.22}
        sparkle={0.18}
        influenceRadius={150}
        influenceStrength={1}
      />

      {/* SCENE */}
      <div className="hero__scene" aria-hidden="true">
        <img className="hero__house" src={House} alt="" />

        {/* keep your wrapper exactly as you have it */}
        <div className="hero__car-wrap">
          <img ref={carRef} className="hero__car" src={Car} alt="" />
        </div>
      </div>



      {/* CONTENT */}
      <section className="hero__content" aria-label="Intro">
        <h1 className="headline anim-fu d1">Sauberkeit, die Eindruck macht</h1>
        <p className="subline anim-fu d2">
          Premium-Reinigung für Zuhause & Unternehmen — präzise, planbar und sichtbar besser.
        </p>

        <ul className="chips anim-fu d3" >
          <li className="chip"><SparkleIcon className="chip__icon" /> Fenster & Glas</li>
          <li className="chip"><OfficeIcon className="chip__icon" /> Büro & Gewerbe</li>
          <li className="chip"><ShieldIcon className="chip__icon" /> Desinfektion</li>
          <li className="chip"><LeafIcon className="chip__icon" /> Grünpflege</li>
        </ul>

        <ul className="points anim-fu d4" >
          <li className="point"><CheckIcon className="point__icon" /> Treppenhaus, Büro, Fenster, Grundreinigung</li>
          <li className="point"><CheckIcon className="point__icon" /> Bauend, Solar, Industrie & Desinfektion</li>
          <li className="point"><CheckIcon className="point__icon" /> Winterdienst, Grünpflege, Hausmeisterservice</li>
        </ul>
      </section>

      <style>{css}</style>
    </main>
  );
}

/* ---------- Icons (unchanged) ---------- */
function CheckIcon({ className }) { return (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);}
function SparkleIcon({ className }) { return (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M12 3l2 4 4 2-4 2-2 4-2-4-4-2 4-2 2-4z" />
    <path d="M19 14l1 2 2 1-2 1-1 2-1-2-2-1 2-1 1-2z" />
  </svg>
);}
function OfficeIcon({ className }) { return (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <rect x="3" y="3" width="10" height="18" rx="1" />
    <rect x="15" y="7" width="6" height="14" rx="1" />
    <path d="M7 7h2M7 11h2M7 15h2" />
  </svg>
);}
function ShieldIcon({ className }) { return (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M12 3l8 3v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6l8-3z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);}
function LeafIcon({ className }) { return (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M4 14c6 3 13 1 16-6 1-3 1-5 0-7-3 2-6 2-9 0C5 5 3 11 6 18" />
    <path d="M5 14c4-1 7-4 9-7" />
  </svg>
);}

/* ================= CSS (no image changes • animation fixed) ================= */
const css = `
:root{
  --nav-h: 0px;

  /* Desktop composition (unchanged) */
  --house-w: 43vw; --house-left: 8vw;  --house-bottom: 22vh;
  --car-w:   52vw; --car-left:  2.8vw; --car-bottom:  -10vh;

  --bg-teal: rgba(10,108,123,0.9);

  /* Your mobile fits (kept as you set them) */
  --m-car-scale: 1.8;
  --m-car-x: -18px;
  --m-car-y: -90px;

  --m-house-scale: 1.8;
  --m-house-x: -10px;
  --m-house-y: 20px;

  --stack-gap: 1px;
}

/* ===== Hero background ===== */
.hero{
  position: relative;
  height: calc(100svh - var(--nav-h));
  min-height: calc(100dvh - var(--nav-h));
  width: 100%;
  overflow: hidden;
  background:
    radial-gradient(120% 120% at 10% 100%, rgba(255,255,255,0.05) 0 30%, transparent 40%),
    radial-gradient(140% 140% at 90% 10%, rgba(255,255,255,0.06) 0 35%, transparent 45%),
    var(--bg-teal);
  isolation: isolate;
  padding-top: max(env(safe-area-inset-top, 0px), 0px);
}

/* ===== Stage ===== */
.hero__scene{
  position: relative;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

/* House (desktop positions) */
.hero__house{
  position: absolute;
  left: var(--house-left);
  bottom: var(--house-bottom);
  width: var(--house-w);
  height: auto;
  z-index: 1;
  -webkit-user-drag: none; user-select: none;
  transform-origin: left bottom;
}

/* Car — wrapper scales/offsets (mobile); img animates translateX */
.hero__car-wrap{
  position: absolute;
  left: var(--car-left);
  bottom: var(--car-bottom);
  width: var(--car-w);
  transform-origin: left bottom;
  z-index: 2;
}
.hero__car{
  display:block;
  width: 100%;
  height: auto;
  -webkit-user-drag: none; user-select: none;
  will-change: transform, opacity;
}

/* ===== Content (desktop right) ===== */
.hero__content{
  position: absolute;
  top: 50%;
  right: clamp(16px, 6vw, 80px);
  transform: translateY(-50%);
  width: min(38vw, 560px);
  padding-left: clamp(12px, 2vw, 28px);
  z-index: 3;
  color: #fff;
  display: grid;
  gap: 16px;
  text-align: left;
}

/* Typography & lists (unchanged) */
.headline{ margin:0; font-weight:900; letter-spacing:-.02em; line-height:.98; font-size: clamp(36px, 6.2vw, 92px); text-shadow: 0 4px 18px rgba(0,0,0,.28); }
.subline{ margin:.2rem 0 .4rem; font-size:clamp(15px,2vw,20px); line-height:1.55; color:rgba(255,255,255,.92); text-shadow:0 2px 10px rgba(0,0,0,.22); max-width:48ch; }
.chips{ display:flex; flex-wrap:wrap; gap:8px 10px; justify-content:flex-start; }
.chip{ display:inline-flex; align-items:center; gap:.5rem; padding:7px 12px; border-radius:999px; border:1.5px solid rgba(255,255,255,.82); color:#fff; font-weight:900; font-size:12px; text-shadow:0 1px 3px rgba(0,0,0,.22); }
.chip__icon{ width:16px; height:16px; color:#10b981; filter: drop-shadow(0 0 6px rgba(16,185,129,.45)); }
.points{ list-style:none; margin:.1rem 0 0; padding:0; display:grid; gap:.45rem; color:rgba(255,255,255,.92); font-size:clamp(14px,1.7vw,18px); }
.point{ display:inline-flex; align-items:center; gap:10px; }
.point__icon{ width:18px; height:18px; color:#10b981; filter: drop-shadow(0 0 4px rgba(16,185,129,.35)); }

/* Fade-up */
.anim-fu { opacity:0; transform:translateY(14px); transition:opacity .5s ease, transform .5s ease; }
.hero-text-in .anim-fu { opacity:1; transform:translateY(0); }
.anim-fu.d1 { transition-delay:.18s; } .anim-fu.d2 { transition-delay:.28s; }
.anim-fu.d3 { transition-delay:.38s; } .anim-fu.d4 { transition-delay:.48s; }

/* =========================
   MOBILE/TABLET
   ========================= */
@media (max-width: 900px){
  .hero{ padding: 0 clamp(12px, 4vw, 24px); }
  .hero__scene{
    position: relative;
    height: clamp(48svh, 56svh, 60svh);
    margin-top: clamp(6px, 1.2vh, 10px);
  }

  /* House mobile fit (kept) */
  .hero__house{
    transform: translate(var(--m-house-x), var(--m-house-y)) scale(var(--m-house-scale));
  }

  /* Car mobile fit on WRAPPER (kept) */
  .hero__car-wrap{
    transform: translate(var(--m-car-x), var(--m-car-y)) scale(var(--m-car-scale));
  }

  /* Your content block — left as-is except closed properly */
  .hero__content{
    position: absolute;
    transform: none;
    width: min(92vw, 680px);
    margin: clamp(-2500px, 1.4vh, 14px);
    display: grid;
    gap: clamp(10px, 2vh, var(--stack-gap));
    text-align: center;
    padding-left: 0;
  }

  .subline{ margin:.15rem auto .35rem; max-width:60ch; }
  .chips{ justify-content:center; }
  .points{ justify-items:center; }
}

/* Drive-in animation on the IMG; wrapper holds mobile scale/offset */
.hero__car.drive{
  animation: car-drive-in 3.2s cubic-bezier(.22,.61,.36,1) forwards;
}

/* One canonical keyframes (desktop & mobile) */
@keyframes car-drive-in{
  0%   { transform: translate3d(-42vw, 0, 0); opacity: 0.001; }
  12%  { opacity: 1; }
  100% { transform: translate3d(0, 0, 0);     opacity: 1; }
}

/* If you had a reduced-motion block cancelling animations, keep drive alive */
@media (prefers-reduced-motion: reduce){
  .hero__car.drive{
    animation: car-drive-in 3.2s (.22,.61,.36,1) forwards !important;
  }
}
`;
